import { keccak_256 } from "@noble/hashes/sha3"
import { base58 } from "@scure/base"
import { config } from "@src/components/DefuseSDK/config"
import type { IntentsUserId } from "@src/components/DefuseSDK/types/intentsUserId"

export const LST_CLAIM_GAS = 80_000_000_000_000n // 80 TGAS, per LST README.
export const UNSTAKE_COOLDOWN_EPOCHS = 4

export interface LstWithdrawalTranche {
  amount: bigint
  unstakeEpoch: number
}

export interface LstCurrentEpochEstimate {
  epochHeight: number
  epochStartedAtMs: number
  estimatedEpochDurationMs: number
  nowMs: number
}

export interface LstUserWithdrawalRequest {
  hash: string
  tranches: LstWithdrawalTranche[]
  totalAmount: bigint
}

function buildIntentsCreditMsg(userId: IntentsUserId): string {
  return JSON.stringify({ receiver_id: userId })
}

export function buildLstWithdrawArgs(userId: IntentsUserId) {
  return {
    receiver_id: config.env.contractID,
    withdraw_tokens: {
      wnear: {
        is_storage_deposit: false,
        msg: buildIntentsCreditMsg(userId),
        memo: null,
        min_gas: null,
      },
    },
  }
}

export function buildStakeMessage(
  userId: IntentsUserId,
  { isStorageDeposit = false }: { isStorageDeposit?: boolean } = {}
): string {
  return JSON.stringify({
    receiver_id: config.env.contractID,
    is_storage_deposit: isStorageDeposit,
    msg: buildIntentsCreditMsg(userId),
    memo: null,
    min_gas: null,
    refund_message: null,
  })
}

export function buildUnstakeMessage(userId: IntentsUserId): string {
  return JSON.stringify(buildLstWithdrawArgs(userId))
}

export function getUnstakeMessageHash(userId: IntentsUserId): string {
  return base58.encode(keccak_256(serializeUnstakeMessage(userId)))
}

export function getClaimableWithdrawalAmount(
  request: LstUserWithdrawalRequest,
  currentEpoch: LstCurrentEpochEstimate | null | undefined
): bigint {
  if (currentEpoch == null) return 0n
  return sumReadyAmount(request, currentEpoch)
}

export function isTrancheReady(
  unstakeEpoch: number,
  currentEpoch: LstCurrentEpochEstimate
): boolean {
  return currentEpoch.epochHeight >= unstakeEpoch + UNSTAKE_COOLDOWN_EPOCHS
}

export function getTrancheReadyAtMs(
  unstakeEpoch: number,
  currentEpoch: LstCurrentEpochEstimate
): number {
  return (
    currentEpoch.epochStartedAtMs +
    (unstakeEpoch + UNSTAKE_COOLDOWN_EPOCHS - currentEpoch.epochHeight) *
      currentEpoch.estimatedEpochDurationMs
  )
}

function serializeUnstakeMessage(userId: IntentsUserId): Uint8Array {
  const intentReceiverMsg = buildIntentsCreditMsg(userId)

  return concatBytes([
    serializeString(config.env.contractID),
    new Uint8Array([1]), // WithdrawTokens::Wnear
    new Uint8Array([0]), // is_storage_deposit: false
    serializeSomeString(intentReceiverMsg),
    serializeNone(),
    serializeNone(),
  ])
}

function sumReadyAmount(
  request: LstUserWithdrawalRequest,
  currentEpoch: LstCurrentEpochEstimate
): bigint {
  return request.tranches.reduce((total, tranche) => {
    return isTrancheReady(tranche.unstakeEpoch, currentEpoch)
      ? total + tranche.amount
      : total
  }, 0n)
}

function serializeSomeString(value: string): Uint8Array {
  return concatBytes([new Uint8Array([1]), serializeString(value)])
}

function serializeNone(): Uint8Array {
  return new Uint8Array([0])
}

function serializeString(value: string): Uint8Array {
  const bytes = new TextEncoder().encode(value)
  return concatBytes([serializeU32(bytes.length), bytes])
}

function serializeU32(value: number): Uint8Array {
  const bytes = new Uint8Array(4)
  new DataView(bytes.buffer).setUint32(0, value, true)
  return bytes
}

function concatBytes(chunks: Uint8Array[]): Uint8Array {
  const size = chunks.reduce((total, chunk) => total + chunk.length, 0)
  const result = new Uint8Array(size)
  let offset = 0

  for (const chunk of chunks) {
    result.set(chunk, offset)
    offset += chunk.length
  }

  return result
}
